package com.siriusxm.pia.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.siriusxm.pia.SXMUI
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.DOMScope
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.renderComposable
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement

/**
 * Portal system for rendering content outside the normal DOM hierarchy.
 * This is essential for popups, tooltips, and dropdowns that need to escape
 * overflow:hidden containers.
 */

private const val PORTAL_ROOT_ID = "sxm-portal-root"

/**
 * Creates or gets the portal root element in document.body
 */
private fun getPortalRoot(): HTMLElement {
    val existing = document.getElementById(PORTAL_ROOT_ID) as? HTMLElement
    if (existing != null) {
        return existing
    }

    val portalRoot = document.createElement("div") as HTMLElement
    portalRoot.id = PORTAL_ROOT_ID
    portalRoot.style.apply {
        position = "absolute"
        top = "0"
        left = "0"
        width = "100%"
        height = "100%"
        zIndex = "1000" // Base z-index for portal content
        setProperty("pointer-events", "none") // Allow clicks to pass through to underlying content
    }
    document.body?.appendChild(portalRoot)
    return portalRoot
}

/**
 * Portal container that should be rendered at the root level of the application.
 * This initializes the portal system.
 */
@Composable
fun PortalContainer() {
    LaunchedEffect(Unit) {
        // Ensure portal root exists
        getPortalRoot()
    }
}

/**
 * Portal component that renders content outside the normal DOM hierarchy.
 *
 * @param isOpen Whether the portal content should be rendered
 * @param onDismiss Callback when the portal should be dismissed (e.g., click outside)
 * @param triggerElement The element that triggered this portal (for positioning)
 * @param popupAlign How to align the popup relative to the trigger element
 * @param content The content to render in the portal
 */
@Composable
fun Portal(
    isOpen: Boolean,
    onDismiss: (() -> Unit)? = null,
    triggerElement: Element? = null,
    popupAlign: PopupAlign = PopupAlign.Left,
    content: @Composable DOMScope<Element>.() -> Unit
) {
    if (isOpen) {
        val portalRoot = remember { getPortalRoot() }

        DisposableEffect(isOpen, triggerElement) {
            // Create a div that will be rendered in the portal root
            val portalDiv = document.createElement("div") as HTMLElement
            portalDiv.setAttribute("data-portal", "true")
            portalDiv.style.apply {
                setProperty("pointer-events", "auto")
                position = "absolute"

                // Calculate position relative to trigger element
                if (triggerElement != null) {
                    val rect = triggerElement.getBoundingClientRect()
                    top = "${rect.bottom + 5}px"

                    when (popupAlign) {
                        PopupAlign.Left -> {
                            left = "${rect.left}px"
                        }

                        PopupAlign.Right -> {
                            right = "${window.innerWidth - rect.right}px"
                        }
                    }
                }
            }
            portalRoot.appendChild(portalDiv)

            // Use renderComposable to render our content into this div
            val composition = renderComposable(portalDiv) {
                Div({
                    if (onDismiss != null) {
                        onClickOutside {
                            onDismiss()
                        }
                    }
                }) {
                    content()
                }
            }

            onDispose {
                composition.dispose()
                if (portalRoot.contains(portalDiv)) {
                    portalRoot.removeChild(portalDiv)
                }
            }
        }
    }
}

/**
 * Internal component that renders the actual portal content
 */
@Composable
private fun PortalContent(
    onDismiss: (() -> Unit)?,
    content: @Composable DOMScope<Element>.() -> Unit
) {
    Div({
        if (onDismiss != null) {
            onClickOutside {
                onDismiss()
            }
        }
        style {
            property("pointer-events", "auto")
        }
    }) {
        content()
    }
}

/**
 * Higher-level portal component for positioned content like popups and dropdowns.
 *
 * @param isOpen Whether the portal should be visible
 * @param onDismiss Callback when clicking outside or pressing escape
 * @param triggerElement The element that triggered this portal (for positioning)
 * @param popupAlign How to align the popup relative to the trigger element
 * @param content The content to render
 */
@Composable
fun PositionedPortal(
    isOpen: Boolean,
    onDismiss: (() -> Unit)? = null,
    triggerElement: Element? = null,
    popupAlign: PopupAlign = PopupAlign.Left,
    content: @Composable DOMScope<Element>.() -> Unit
) {
    Portal(isOpen, onDismiss, triggerElement, popupAlign) {
        Div({
            style {
                backgroundColor(SXMUI.containerContentBackgroundColor.value())
                shadowBorder()
                borderRadius(SXMUI.boxRadius.value())
                minWidth(100.px)
                overflow("hidden")
                property("z-index", ZIndexLayer.POPUP.toCssZIndex())
            }
        }) {
            content()
        }
    }
}